// Google Sheets Integration
// This uses a Google Apps Script Web App as a bridge to write to Google Sheets

export interface GoogleSheetsConfig {
  webAppUrl: string
  sheetName: string
  enabled: boolean
}

export interface AppointmentRow {
  id: string
  patientName: string
  doctorName: string
  serviceName: string
  date: string
  time: string
  duration: number
  status: string
  notes: string
  createdAt: string
  updatedAt: string
}

class GoogleSheetsService {
  private config: GoogleSheetsConfig | null = null

  constructor() {
    this.loadConfig()
  }

  private loadConfig() {
    const saved = localStorage.getItem('savidna-google-sheets-config')
    if (saved) {
      try {
        this.config = JSON.parse(saved)
      } catch (e) {
        console.error('Error loading Google Sheets config:', e)
      }
    }
  }

  saveConfig(config: GoogleSheetsConfig) {
    this.config = config
    localStorage.setItem('savidna-google-sheets-config', JSON.stringify(config))
  }

  getConfig(): GoogleSheetsConfig | null {
    return this.config
  }

  isEnabled(): boolean {
    return this.config?.enabled && !!this.config?.webAppUrl
  }

  async syncAppointment(
    action: 'create' | 'update' | 'delete',
    appointmentData: AppointmentRow,
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled() || !this.config) {
      return {
        success: false,
        error: 'Google Sheets integration not configured',
      }
    }

    try {
      const response = await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors', // Google Apps Script requires no-cors
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          sheetName: this.config.sheetName,
          data: appointmentData,
        }),
      })

      // Note: no-cors mode doesn't allow reading the response
      // We assume success if no error is thrown
      return { success: true }
    } catch (error) {
      console.error('Error syncing to Google Sheets:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async syncAllAppointments(
    appointments: AppointmentRow[],
  ): Promise<{ success: boolean; error?: string }> {
    if (!this.isEnabled() || !this.config) {
      return {
        success: false,
        error: 'Google Sheets integration not configured',
      }
    }

    try {
      const response = await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'sync_all',
          sheetName: this.config.sheetName,
          data: appointments,
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error syncing all appointments to Google Sheets:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    if (!this.config?.webAppUrl) {
      return { success: false, error: 'No Web App URL configured' }
    }

    try {
      const response = await fetch(this.config.webAppUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'test',
          sheetName: this.config.sheetName,
        }),
      })

      return { success: true }
    } catch (error) {
      console.error('Error testing Google Sheets connection:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      }
    }
  }

  getSetupInstructions(): string {
    return `
# Configuración de Google Sheets

## Paso 1: Crear una nueva hoja de cálculo
1. Ve a Google Sheets (https://sheets.google.com)
2. Crea una nueva hoja de cálculo
3. Nómbrala "Savidna Citas" o el nombre que prefieras

## Paso 2: Crear un Google Apps Script
1. En tu hoja, ve a **Extensiones → Apps Script**
2. Borra el código existente
3. Pega el siguiente código:

\`\`\`javascript
function doPost(e) {
  try {
    var data = JSON.parse(e.postData.contents);
    var sheetName = data.sheetName || 'Citas';
    var ss = SpreadsheetApp.getActiveSpreadsheet();
    var sheet = ss.getSheetByName(sheetName);
    
    // Create sheet if it doesn't exist
    if (!sheet) {
      sheet = ss.insertSheet(sheetName);
      // Add headers
      sheet.appendRow([
        'ID', 'Paciente', 'Doctor', 'Servicio', 'Fecha', 
        'Hora', 'Duración (min)', 'Estado', 'Notas', 
        'Creado', 'Actualizado'
      ]);
      
      // Format header row
      var headerRange = sheet.getRange(1, 1, 1, 11);
      headerRange.setBackground('#4285f4');
      headerRange.setFontColor('#ffffff');
      headerRange.setFontWeight('bold');
      sheet.setFrozenRows(1);
    }
    
    if (data.action === 'test') {
      return ContentService.createTextOutput(JSON.stringify({
        success: true,
        message: 'Connection successful'
      })).setMimeType(ContentService.MimeType.JSON);
    }
    
    if (data.action === 'sync_all') {
      // Clear existing data (except headers)
      if (sheet.getLastRow() > 1) {
        sheet.deleteRows(2, sheet.getLastRow() - 1);
      }
      
      // Add all appointments
      if (data.data && data.data.length > 0) {
        var rows = data.data.map(function(apt) {
          return [
            apt.id,
            apt.patientName,
            apt.doctorName,
            apt.serviceName,
            apt.date,
            apt.time,
            apt.duration,
            apt.status,
            apt.notes,
            apt.createdAt,
            apt.updatedAt
          ];
        });
        sheet.getRange(2, 1, rows.length, 11).setValues(rows);
      }
      
      return ContentService.createTextOutput(JSON.stringify({
        success: true,
        message: 'All appointments synced'
      })).setMimeType(ContentService.MimeType.JSON);
    }
    
    if (data.action === 'create') {
      var apt = data.data;
      sheet.appendRow([
        apt.id,
        apt.patientName,
        apt.doctorName,
        apt.serviceName,
        apt.date,
        apt.time,
        apt.duration,
        apt.status,
        apt.notes,
        apt.createdAt,
        apt.updatedAt
      ]);
    }
    
    if (data.action === 'update') {
      var apt = data.data;
      var dataRange = sheet.getDataRange();
      var values = dataRange.getValues();
      
      for (var i = 1; i < values.length; i++) {
        if (values[i][0] === apt.id) {
          sheet.getRange(i + 1, 1, 1, 11).setValues([[
            apt.id,
            apt.patientName,
            apt.doctorName,
            apt.serviceName,
            apt.date,
            apt.time,
            apt.duration,
            apt.status,
            apt.notes,
            apt.createdAt,
            apt.updatedAt
          ]]);
          break;
        }
      }
    }
    
    if (data.action === 'delete') {
      var apt = data.data;
      var dataRange = sheet.getDataRange();
      var values = dataRange.getValues();
      
      for (var i = 1; i < values.length; i++) {
        if (values[i][0] === apt.id) {
          sheet.deleteRow(i + 1);
          break;
        }
      }
    }
    
    return ContentService.createTextOutput(JSON.stringify({
      success: true
    })).setMimeType(ContentService.MimeType.JSON);
    
  } catch (error) {
    return ContentService.createTextOutput(JSON.stringify({
      success: false,
      error: error.toString()
    })).setMimeType(ContentService.MimeType.JSON);
  }
}
\`\`\`

4. Guarda el proyecto (Ctrl+S o Cmd+S)

## Paso 3: Desplegar como Web App
1. Click en **Desplegar** (botón azul arriba a la derecha)
2. Selecciona **Nueva implementación**
3. En "Tipo", selecciona **Aplicación web**
4. Configuración:
   - **Ejecutar como**: Yo (tu correo)
   - **Quién tiene acceso**: Cualquier persona
5. Click en **Desplegar**
6. Autoriza los permisos cuando se solicite
7. **COPIA LA URL** que aparece (empieza con https://script.google.com/macros/s/...)

## Paso 4: Configurar en Savidna
1. Pega la URL de la Web App en el campo "URL de Google Apps Script Web App"
2. Ingresa el nombre de la hoja (por defecto: "Citas")
3. Activa la integración con el toggle
4. Click en "Guardar Configuración"
5. Click en "Probar Conexión" para verificar

## Paso 5: Sincronizar datos existentes
1. Una vez configurado y probado
2. Ve a la pestaña de Citas en Savidna
3. Las nuevas citas se sincronizarán automáticamente
4. Para sincronizar citas existentes, puedes crear una nueva cita de prueba

¡Listo! Ahora todas las citas se sincronizarán automáticamente con Google Sheets.
    `.trim()
  }
}

export const googleSheetsService = new GoogleSheetsService()
